import { ThirdsRepository } from '../../domain/models/thirds/gateway/thirds-repository';
import { Third, CatalogItem, Municipality } from '../../domain/models/thirds/third';
import HttpClient from '../../../utils/http-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export default class ThirdsRepositoryImp extends ThirdsRepository {
  get(uid: string, cid: string | null, type: string, page = 1, limit = 10, search: any[] = []): Observable<Third[]> {
    const httpClient = new HttpClient(uid, cid);
    const typeParam = type !== 'all' ? `type=${type}&` : '';
    const queryString = search
      .filter(f => f.label !== 'type')
      .reduce((acc, f, i) => acc + `&${f.label}=${encodeURIComponent(f.value)}`, '');
    return httpClient.get<Third[]>(`thirds?${typeParam}page=${page}&limit=${limit}${queryString}`);
  }

  create(uid: string, cid: string | null, data: Third, type: string): Observable<any> {
    const httpClient = new HttpClient(uid, cid);
    const payload = this.mapToBackend(data);
    return httpClient.post<any>(`thirds`, payload);
  }

  edit(uid: string, cid: string | null, data: Third, id: number): Observable<any> {
    const httpClient = new HttpClient(uid, cid);
    const payload = this.mapToBackend(data);
    return httpClient.put<any>(`thirds/${id}`, payload);
  }

  getById(uid: string, cid: string | null, id: number | string): Observable<Third> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.get<Third>(`thirds/${id}`);
  }

  getByIdentification(uid: string, cid: string | null, identification: string): Observable<Third> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.get<Third>(`thirds?query=${identification}`);
  }

  delete(uid: string, cid: string | null, id: number | string): Observable<any> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.delete<any>(`thirds/${id}`);
  }

  inactivate(uid: string, cid: string | null, id: number | string): Observable<any> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.put<any>(`thirds/${id}/status`, { status: "INACTIVE" });
  }

  reactivate(uid: string, cid: string | null, id: number | string): Observable<any> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.put<any>(`thirds/${id}/status`, { status: "ACTIVE" });
  }

  // Catalogs Implementation
  getIdentificationTypes(uid: string, cid: string | null): Observable<CatalogItem[]> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.get<any>(`thirds/catalogs/identification-types`).pipe(
      map(res => res.data || res)
    );
  }

  getOrganizations(uid: string, cid: string | null): Observable<CatalogItem[]> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.get<any>(`thirds/catalogs/organizations`).pipe(
      map(res => res.data || res)
    );
  }

  getLiabilities(uid: string, cid: string | null): Observable<CatalogItem[]> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.get<any>(`thirds/catalogs/liabilities`).pipe(
      map(res => res.data || res)
    );
  }

  getRegimes(uid: string, cid: string | null): Observable<CatalogItem[]> {
    const httpClient = new HttpClient(uid, cid);
    return httpClient.get<any>(`thirds/catalogs/regimes`).pipe(
      map(res => res.data || res)
    );
  }

  getMunicipalities(uid: string, cid: string | null, query?: string): Observable<Municipality[]> {
    const httpClient = new HttpClient(uid, cid);
    const url = `thirds/catalogs/municipalities${query ? `?query=${query}` : ''}`;
    return httpClient.get<any>(url).pipe(
      map(res => res.data || res)
    );
  }

  private mapToBackend(data: Third): any {
    return {
      name: data.name,
      email: data.email,
      phone: data.phone,
      identification: data.identification,
      identifications_id: data.identificationsId,
      address: data.address,
      municipality_id: data.municipalityId,
      type_organization_id: data.typeOrganizationId,
      type_liability_id: data.typeLiabilityId,
      type_regime_id: data.typeRegimeId,
      postal_zone_code: data.postalZoneCode,
      merchant_registration: data.merchantRegistration,
      dv: data.dv,
      country_id: data.countryId,
      client: data.client,
      provider: data.provider,
      other: data.other
    };
  }
}
